/**
 * Opening-hours evaluation.
 *
 * This mirrors `private.opening_window_at` in the database. The server is the
 * authority — it is what actually accepts or rejects an order — so this exists
 * to render "Open until 21:00" and to grey out impossible pickup slots without
 * a round trip. When the two disagree, the server's answer is the correct one.
 */

/** 0 = Sunday, matching Postgres `extract(dow)`. */
export type Weekday = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export interface OpeningHour {
  weekday: number;
  is_closed: boolean;
  open_time: string | null;
  close_time: string | null;
}

export interface OpeningException {
  on_date: string;
  is_closed: boolean;
  open_time: string | null;
  close_time: string | null;
  note?: string | null;
}

export interface WeeklyHours {
  timezone: string;
  hours: OpeningHour[];
  exceptions?: OpeningException[];
}

/** One trading window on a calendar date, in minutes from local midnight. */
export interface DayWindow {
  openMinutes: number;
  closeMinutes: number;
}

/**
 * Whether a window's close is at or before its open, so it runs past midnight.
 *
 * @param window The window to test.
 * @returns True when the window crosses into the following day.
 */
function wrapsMidnight(window: DayWindow): boolean {
  return window.closeMinutes <= window.openMinutes;
}

/**
 * English short weekday names, used only to parse `Intl`'s output back into an
 * index. The formatter is pinned to `en-US` for exactly this reason: parsing a
 * localised name would need a per-locale table.
 */
const SHORT_WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** 2024-01-07 was a Sunday, used as the anchor for weekday name lookups. */
const SUNDAY_ANCHOR_UTC = Date.UTC(2024, 0, 7);

/**
 * The display name of a weekday index, in the target locale.
 *
 * Uses `Intl` rather than a hand-written table so a new locale gets correct
 * weekday names without touching this file.
 *
 * @param weekday 0 = Sunday through 6 = Saturday.
 * @param short When true, returns the abbreviated form.
 * @param locale Target locale.
 * @returns The localised weekday name.
 */
export function weekdayName(weekday: number, short = false, locale: string = 'en'): string {
  const date = new Date(SUNDAY_ANCHOR_UTC + ((weekday % 7) + 7) % 7 * 86_400_000);
  return new Intl.DateTimeFormat(locale, {
    timeZone: 'UTC',
    weekday: short ? 'short' : 'long',
  }).format(date);
}

/**
 * Parses a Postgres `time` literal into minutes since local midnight.
 *
 * @param value `HH:MM` or `HH:MM:SS`, optionally with fractional seconds.
 * @returns Minutes since midnight, or null when the value is absent or invalid.
 */
export function parseTimeToMinutes(value: string | null | undefined): number | null {
  if (!value) return null;
  const match = /^(\d{1,2}):(\d{2})/.exec(value);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  // `24:00` is the end of a day, and the column accepts it — a save made before
  // the form wrote `00:00` may hold one. It is 1440 minutes rather than a bad
  // hour, so it parses: refusing it dropped the whole window and made the day
  // read as closed while the server was still taking orders for it.
  if (hours > 24 || minutes > 59) return null;
  if (hours === 24 && minutes !== 0) return null;
  return hours * 60 + minutes;
}

/**
 * Renders minutes since midnight as `HH:MM`.
 *
 * @param minutes Minutes since local midnight; values at or beyond 1440 wrap.
 * @returns A 24-hour clock string.
 */
export function minutesToTime(minutes: number): string {
  const normalised = ((minutes % 1440) + 1440) % 1440;
  const h = Math.floor(normalised / 60);
  const m = normalised % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

interface ZonedParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
  weekday: number;
}

/**
 * Breaks an instant into calendar fields as observed in a specific IANA zone.
 *
 * Uses `Intl.DateTimeFormat.formatToParts` rather than the host's local time,
 * so a merchant in Singapore and a customer travelling abroad both see the
 * restaurant's hours rather than their own.
 *
 * @param instant The moment to decompose.
 * @param timeZone IANA zone name, for example `Asia/Singapore`.
 * @returns Calendar fields in that zone.
 */
export function zonedParts(instant: Date, timeZone: string): ZonedParts {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    weekday: 'short',
  });

  const parts: Record<string, string> = {};
  for (const part of formatter.formatToParts(instant)) {
    parts[part.type] = part.value;
  }

  const weekdayIndex = SHORT_WEEKDAYS.indexOf(parts.weekday ?? 'Sun');

  return {
    year: Number(parts.year),
    month: Number(parts.month),
    day: Number(parts.day),
    // Some engines emit hour 24 for midnight even with hourCycle h23.
    hour: Number(parts.hour) % 24,
    minute: Number(parts.minute),
    second: Number(parts.second),
    weekday: weekdayIndex < 0 ? 0 : weekdayIndex,
  };
}

/**
 * The calendar date in a zone, as `YYYY-MM-DD`.
 *
 * @param instant The moment to convert.
 * @param timeZone IANA zone name.
 * @returns An ISO calendar date.
 */
export function localDateKey(instant: Date, timeZone: string): string {
  const p = zonedParts(instant, timeZone);
  return `${p.year}-${String(p.month).padStart(2, '0')}-${String(p.day).padStart(2, '0')}`;
}

/**
 * Minutes since local midnight in a zone.
 *
 * @param instant The moment to convert.
 * @param timeZone IANA zone name.
 * @returns Minutes since midnight, 0 through 1439.
 */
export function localMinutes(instant: Date, timeZone: string): number {
  const p = zonedParts(instant, timeZone);
  return p.hour * 60 + p.minute;
}

/**
 * Shifts a `YYYY-MM-DD` key by a whole number of days.
 *
 * @param dateKey Source calendar date.
 * @param days Signed day offset.
 * @returns The shifted calendar date.
 */
export function shiftDateKey(dateKey: string, days: number): string {
  const [y, m, d] = dateKey.split('-').map(Number);
  const shifted = new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1));
  shifted.setUTCDate(shifted.getUTCDate() + days);
  return shifted.toISOString().slice(0, 10);
}

/**
 * The weekday index of a `YYYY-MM-DD` key, treated as a pure calendar date.
 *
 * @param dateKey Source calendar date.
 * @returns 0 = Sunday through 6 = Saturday.
 */
export function weekdayOfDateKey(dateKey: string): number {
  const [y, m, d] = dateKey.split('-').map(Number);
  return new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1)).getUTCDay();
}

/**
 * Every trading window that starts on one calendar date, in clock order.
 *
 * A day may have several — lunch and dinner are two rows — so this returns a
 * list rather than the one window the product used to assume. Empty means
 * closed: the date's override is a closure, the weekday carries a closure
 * marker, or nothing is stored for it at all.
 *
 * @param hours The restaurant's weekly hours and exceptions.
 * @param dateKey The local calendar date.
 * @returns The windows that start that day; empty when closed.
 */
export function dayWindows(hours: WeeklyHours, dateKey: string): DayWindow[] {
  const exception = hours.exceptions?.find((e) => e.on_date === dateKey);

  const rows = exception
    ? [exception]
    : hours.hours.filter((h) => h.weekday === weekdayOfDateKey(dateKey));

  // A closure marker closes the whole day, whatever else shares the weekday.
  if (rows.length === 0 || rows.some((row) => row.is_closed)) return [];

  return rows
    .map((row) => ({
      openMinutes: parseTimeToMinutes(row.open_time),
      closeMinutes: parseTimeToMinutes(row.close_time),
    }))
    .filter((window): window is DayWindow => window.openMinutes !== null && window.closeMinutes !== null)
    .sort((a, b) => a.openMinutes - b.openMinutes);
}

/**
 * Whether the restaurant is open at an instant, including windows that wrap
 * past midnight.
 *
 * @param hours The restaurant's weekly hours and exceptions.
 * @param instant The moment to test.
 * @returns True when `instant` falls inside a trading window.
 */
export function isOpenAt(hours: WeeklyHours, instant: Date): boolean {
  const nowMinutes = localMinutes(instant, hours.timezone);
  const todayKey = localDateKey(instant, hours.timezone);

  // Today's windows first, then yesterday's — the latter covers a 18:00-02:00
  // service that is still running in the small hours of today.
  for (const offset of [0, -1]) {
    const key = shiftDateKey(todayKey, offset);

    for (const window of dayWindows(hours, key)) {
      if (offset === 0) {
        const end = wrapsMidnight(window) ? window.closeMinutes + 1440 : window.closeMinutes;
        if (nowMinutes >= window.openMinutes && nowMinutes < end) return true;
      } else {
        // Only a wrapping window reaches into today.
        if (!wrapsMidnight(window)) continue;
        if (nowMinutes < window.closeMinutes) return true;
      }
    }
  }

  return false;
}

/**
 * The trading window containing an instant.
 *
 * @param hours The restaurant's weekly hours and exceptions.
 * @param instant The moment to test.
 * @returns The window bounds as instants, or null when closed.
 */
export function currentWindow(hours: WeeklyHours, instant: Date): { start: Date; end: Date } | null {
  if (!isOpenAt(hours, instant)) return null;

  const nowMinutes = localMinutes(instant, hours.timezone);
  const todayKey = localDateKey(instant, hours.timezone);

  for (const offset of [0, -1]) {
    const key = shiftDateKey(todayKey, offset);
    const base = offset === 0 ? todayKey : key;

    for (const window of dayWindows(hours, key)) {
      const wrap = wrapsMidnight(window);
      const startMinutes = offset === 0 ? window.openMinutes : window.openMinutes - 1440;
      const endMinutes = offset === 0 ? (wrap ? window.closeMinutes + 1440 : window.closeMinutes) : window.closeMinutes;

      if (nowMinutes >= startMinutes && nowMinutes < endMinutes) {
        return {
          start: zonedTimeToUtc(base, window.openMinutes, hours.timezone),
          end: zonedTimeToUtc(base, wrap ? window.closeMinutes + 1440 : window.closeMinutes, hours.timezone),
        };
      }
    }
  }

  return null;
}

/**
 * Converts a wall-clock time in a zone to the instant it denotes.
 *
 * Applies the zone offset twice: once from a naive guess and once from the
 * corrected instant. That converges across daylight-saving transitions, where a
 * single pass can land an hour off.
 *
 * @param dateKey Local calendar date.
 * @param minutes Minutes since local midnight; may exceed 1440 to cross midnight.
 * @param timeZone IANA zone name.
 * @returns The corresponding instant.
 */
export function zonedTimeToUtc(dateKey: string, minutes: number, timeZone: string): Date {
  const [y, m, d] = dateKey.split('-').map(Number);
  const naive = Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1) + minutes * 60_000;

  const offsetAt = (ms: number): number => {
    const p = zonedParts(new Date(ms), timeZone);
    const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
    return asUtc - ms;
  };

  let ts = naive - offsetAt(naive);
  ts = naive - offsetAt(ts);
  return new Date(ts);
}

/**
 * The next instant the restaurant opens, at or after a moment.
 *
 * @param hours The restaurant's weekly hours and exceptions.
 * @param from The moment to search from.
 * @param maxDays How far ahead to search before giving up.
 * @returns The next opening instant, or null when closed for the whole horizon.
 */
export function nextOpeningAt(hours: WeeklyHours, from: Date, maxDays = 21): Date | null {
  if (isOpenAt(hours, from)) return from;

  const startKey = localDateKey(from, hours.timezone);

  for (let offset = 0; offset <= maxDays; offset += 1) {
    const key = shiftDateKey(startKey, offset);

    // Every window that has not started yet, earliest first: standing in the
    // break at 16:00, today's 17:30 dinner is the next opening.
    for (const window of dayWindows(hours, key)) {
      const opening = zonedTimeToUtc(key, window.openMinutes, hours.timezone);
      if (opening.getTime() > from.getTime()) return opening;
    }
  }

  return null;
}

/**
 * The trading window for one day, or null when closed.
 *
 * Returns null rather than the string "Closed" so the caller can render the
 * localised word. The time range itself is numerals and a dash, which needs no
 * translation; only the overnight marker does, and that is supplied by the
 * caller via {@link describeDayWith}.
 *
 * A close of `00:00` is the end of the day, and the 24-hour clock already has a
 * numeral for that: `24:00`. Written as `00:00` it says the opposite of what is
 * meant — midnight at the end of today is not midnight at the start of
 * tomorrow — and then needs a marker to take it back. `24:00` needs neither.
 *
 * @param hour The day's rule; null when the restaurant does not trade that day.
 * @returns `09:00 – 21:00`, or null when closed.
 */
export function describeDay(rows: OpeningHour[] | null | undefined): string | null {
  const windows = windowsOf(rows);
  if (windows.length === 0) return null;

  // The overnight marker is deliberately absent: it is prose, so the caller
  // supplies the localised form through `describeDayWith`.
  return windows.map((window) => range(window)).join(', ');
}

/** `09:00 – 21:00`, with the end of the day named `24:00` rather than `00:00`. */
function range(window: DayWindow): string {
  // Both spellings of the end of a day: `00:00`, which the form writes, and
  // `24:00`, which the column accepts and a row may still hold.
  const end = window.closeMinutes % 1440 === 0 ? '24:00' : minutesToTime(window.closeMinutes);
  return `${minutesToTime(window.openMinutes)} – ${end}`;
}

/** Parses the stored rows of one day into windows, dropping unusable ones. */
function windowsOf(rows: OpeningHour[] | null | undefined): DayWindow[] {
  if (!rows || rows.length === 0 || rows.some((row) => row.is_closed)) return [];

  return rows
    .map((row) => ({
      openMinutes: parseTimeToMinutes(row.open_time),
      closeMinutes: parseTimeToMinutes(row.close_time),
    }))
    .filter((window): window is DayWindow => window.openMinutes !== null && window.closeMinutes !== null)
    .sort((a, b) => a.openMinutes - b.openMinutes);
}

/**
 * A fully localised day summary.
 *
 * Every window the day has, in order. A day with a lunch and a dinner reads
 * `11:00 – 14:30, 17:30 – 22:00`, which is the whole reason a day can have more
 * than one.
 *
 * @param rows The day's rows, one per window; an empty list or a closure marker
 *   for a closed day.
 * @param closedLabel The word for "closed" in the active locale.
 * @param overnightSuffix The marker for a window that crosses midnight.
 * @returns A display string, never null.
 */
export function describeDayWith(
  rows: OpeningHour[] | null | undefined,
  closedLabel: string,
  overnightSuffix = ' (+1 day)',
): string {
  const windows = windowsOf(rows);
  if (windows.length === 0) return closedLabel;

  return windows
    .map((window) => {
      // `24:00` is excluded because it has already said the window runs to the
      // end of the day; the marker is for the numerals that do not, like
      // `00:30`, which is a real time on the next day and cannot say so itself.
      const crosses = window.closeMinutes !== 0 && wrapsMidnight(window);
      return crosses ? `${range(window)}${overnightSuffix}` : range(window);
    })
    .join(', ');
}

/**
 * The rows that apply to one calendar date, for a caller that needs to hand
 * them to {@link describeDayWith}: the date's override when there is one, and
 * otherwise every row stored against that weekday.
 *
 * @param hours The weekly hours.
 * @param dateKey The local calendar date.
 * @returns The rows for that date, in stored order.
 */
export function hoursOn(hours: WeeklyHours, dateKey: string): OpeningHour[] {
  const exception = hours.exceptions?.find((e) => e.on_date === dateKey);
  if (exception) {
    return [
      {
        weekday: weekdayOfDateKey(dateKey),
        is_closed: exception.is_closed,
        open_time: exception.open_time,
        close_time: exception.close_time,
      },
    ];
  }

  return hours.hours.filter((h) => h.weekday === weekdayOfDateKey(dateKey));
}

/**
 * Groups consecutive days that share the same hours, which is how opening hours
 * are normally printed.
 *
 * @param hours The weekly hours.
 * @returns Runs of consecutive weekdays with identical rules.
 */
export function groupHours(
  hours: OpeningHour[],
  locale: string = 'en',
): Array<{ days: string; label: string | null }> {
  // One entry per weekday, carrying that weekday's windows: several rows can
  // share a weekday now, and grouping them by their own weekday would print the
  // same day more than once.
  const byWeekday = new Map<number, OpeningHour[]>();
  for (const hour of hours) {
    byWeekday.set(hour.weekday, [...(byWeekday.get(hour.weekday) ?? []), hour]);
  }

  const groups: Array<{ days: string; label: string | null }> = [];

  for (const weekday of [...byWeekday.keys()].sort((a, b) => a - b)) {
    const label = describeDay(byWeekday.get(weekday));
    const last = groups[groups.length - 1];
    if (last && last.label === label) {
      const [start] = last.days.split('–');
      last.days = `${start}–${weekdayName(weekday, true, locale)}`;
      continue;
    }
    groups.push({ days: weekdayName(weekday, true, locale), label });
  }

  return groups;
}
